import { Link, useLocation, useNavigate } from 'react-router-dom';
import React, { FunctionComponent, useContext } from 'react';

import AddDonateButton from '../../components/AddDonateButton/AddDonateButton';
import { AuthContext } from '@justgiving/auth-react';
import DonateBoxMessage from '../../components/DonateBoxMessage/DonateBoxMessage';
import GivingCheckoutLink from '../../components/GivingCheckoutLink/GivingCheckoutLink';
import { PAGES } from '../../constants/pages';
import QRCode from '../../components/QRCode/QRCode';
import Steps from '../../components/Steps/Steps';
import { createGraphQlClient } from '../../graphQl/baseApi';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { useDonationLinkQuery } from '../../../types/__generated-graphQL__';
import { useHeadingSetFocusRef } from '../../hooks/useHeadingSetFocusRef';
import { useQueryParameters } from '../../hooks/useQueryParameters';
import { useTranslation } from 'react-i18next';

const LinkDetails: FunctionComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const headingRef = useHeadingSetFocusRef();
  const query = useQueryParameters();
  const auth = useContext(AuthContext);

  const linkId = query.get('id');

  const { isLoading, data } = useDonationLinkQuery(
    createGraphQlClient(auth),
    {
      input: { donationLinkId: linkId ?? '' },
    },
    {
      enabled: Boolean(linkId),
    },
  );
  const customLink = data?.donationLink?.url;
  const donationLinkId = data?.donationLink?.donationLinkId;
  const isDefault = Boolean(data?.donationLink?.isDefault);

  useDocumentTitle(t('linkDetails.heading'));

  if (isLoading || !customLink) return null;

  return (
    <div className="giving-checkout-page">
      <div className="giving-checkout-page-form">
        <Steps start={5} end={5} showAllDoneMessage />
        <h1 className="jg-h3" ref={headingRef} tabIndex={-1}>
          {t('linkDetails.heading')}
        </h1>
        <p className="jg-space-mbmd jg-color--empress jg-font-weight-regular">
          {t('linkDetails.description')}
        </p>
        <button
          className="giving-checkout-button-link-left-aligned"
          type="button"
          onClick={() =>
            navigate(`${PAGES.PostDonationDetails}${location.search}`)
          }
        >
          {t('linkDetails.back')}
        </button>
        <Link
          to={PAGES.LinkLibrary}
          className="giving-checkout-button-link-left-aligned"
        >
          {t('linkDetails.viewAll')}
        </Link>
      </div>
      <div className="giving-checkout-page-preview">
        <div className="giving-checkout-link-details-wrapper">
          <section className="content-box">
            <h2 className="jg-h3">{t('linkDetails.link.heading')}</h2>
            <p className="jg-color--empress jg-font-weight-regular">
              {t('linkDetails.link.description')}
            </p>
            <GivingCheckoutLink id="multi" link={customLink} />
          </section>
          <section className="content-box" data-qa="add-donate-button">
            <h2 className="jg-h3">{t('linkDetails.button.heading')}</h2>
            <AddDonateButton
              codeBlockType="multi"
              linkId={donationLinkId}
              isDefault={isDefault}
            />
          </section>
          <section className="content-box" data-qa="qr-code-section">
            <h2 className="jg-h3">{t('linkDetails.qrcode.heading')}</h2>
            <QRCode link={customLink} />
          </section>
          <DonateBoxMessage />
        </div>
      </div>
    </div>
  );
};

export default LinkDetails;
